import React, { useMemo } from 'react';

import { Portal, Typography, makeStyles } from '@material-ui/core';
import clsx from 'clsx';

import { formatElapsedTime } from 'constants/dictionaries/sheetsTimer';
import { FEATURE_FLAGS } from 'constants/dictionaries/featureFlags';
import LAYOUT_SIZES from 'constants/styles/LAYOUT_SIZES';
import { useFeatureFlag } from 'hooks/useFeatureFlags/useFeatureFlags';
import useFeatureChecker from 'services/common/useFeatureChecker/useFeatureChecker';
import useSheetsStore from 'storages/sheets/sheets';
import useSheetsTimerStore from 'storages/sheetsTimer/sheetsTimer';
import ExerciseViewTile from 'components/ExerciseViewTile/ExerciseViewTile';

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    minWidth: '200px',
    marginRight: theme.spacing(4),
    padding: theme.spacing(1, 2),
    transition: 'height .3s ease, padding .3s ease',
    height: ({ isStuck }: { isStuck: boolean }) => (isStuck ? '50px' : '95px'),
    [theme.breakpoints.down('md')]: {
      minWidth: 'auto',
      marginRight: 0,
      height: 'auto',
      padding: theme.spacing(0.5, 1),
    },
  },
  label: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  time: {
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.2,
  },
  overLimit: {
    color: theme.palette.warning.dark,
  },
  pomoceWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  mobileFixedWrapper: {
    position: 'fixed',
    bottom: LAYOUT_SIZES.FOOTER.sm,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 2),
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.shadows[2],
    zIndex: 101,
  },
}));

export const SHEETS_TIMER_MOBILE_BAR_HEIGHT = 36;

export const useIsSheetsTimerActive = (): boolean => {
  const isEnabled = useFeatureFlag(FEATURE_FLAGS.SHEETS_TIMER);
  const { isSheet } = useFeatureChecker();
  const { fetched } = useSheetsStore();
  const { durationMinutes, isRunning } = useSheetsTimerStore();

  return isEnabled && isSheet && fetched && durationMinutes != null && isRunning;
};

type Props = {
  isStuck?: boolean;
  variant?: 'desktop' | 'pomoce' | 'mobile-fixed';
};

const SheetsTimerDisplay: React.FC<Props> = ({ isStuck = false, variant = 'desktop' }) => {
  const isActive = useIsSheetsTimerActive();
  const { durationMinutes, elapsedSeconds } = useSheetsTimerStore();
  const classes = useStyles({ isStuck });

  const isOverLimit = useMemo(() => {
    if (durationMinutes == null) {
      return false;
    }

    return elapsedSeconds > durationMinutes * 60;
  }, [durationMinutes, elapsedSeconds]);

  if (!isActive) {
    return null;
  }

  const formattedTime = formatElapsedTime(elapsedSeconds);

  if (variant === 'mobile-fixed') {
    return (
      <Portal>
        <div className={classes.mobileFixedWrapper}>
          <Typography className={classes.label} component='span'>
            Czas pracy:
          </Typography>
          <Typography className={clsx(classes.time, isOverLimit && classes.overLimit)} color='primary' component='span'>
            {formattedTime}
          </Typography>
        </div>
      </Portal>
    );
  }

  if (variant === 'pomoce') {
    return (
      <div className={classes.pomoceWrapper}>
        <Typography className={classes.label} component='span'>
          Czas pracy
        </Typography>
        <Typography className={clsx(classes.time, isOverLimit && classes.overLimit)} color='primary' component='span'>
          {formattedTime}
        </Typography>
      </div>
    );
  }

  return (
    <ExerciseViewTile className={classes.wrapper}>
      <Typography className={classes.label} component='span'>
        Czas pracy
      </Typography>
      <Typography className={clsx(classes.time, isOverLimit && classes.overLimit)} color='primary' component='span'>
        {formattedTime}
      </Typography>
    </ExerciseViewTile>
  );
};

export default SheetsTimerDisplay;
